/**
 * JSON-LD builders, one per shape the site actually supports.
 *
 * Every URL is absolute and on the apex domain, and the entities are linked by
 * `@id` so Organization and WebSite are declared once and referenced from the
 * pages that need them.
 *
 * What is deliberately absent: `aggregateRating`, `review`, `sameAs`, and any
 * customer or user count. DebtDrone has no public ratings, no published social
 * profiles and no named customers, and structured data that claims otherwise is
 * a fabrication regardless of how well it would render. Prices come only from
 * tiers the page is showing.
 */

import { SITE_URL } from './public-routes';

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

const ORGANIZATION_NAME = 'DebtDrone';

export interface JsonLd {
  '@context'?: string;
  '@type': string;
  [key: string]: unknown;
}

/** No `sameAs`: DebtDrone has no public social or GitHub-org profiles to link to. */
export function organizationJsonLd(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: ORGANIZATION_NAME,
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/debtdrone-logo.png`,
  };
}

export function webSiteJsonLd(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: ORGANIZATION_NAME,
    url: `${SITE_URL}/`,
    publisher: { '@id': ORGANIZATION_ID },
  };
}

export interface SoftwareOffer {
  name: string;
  /** Omit a tier with no published price rather than inventing one. */
  price: number;
  priceCurrency: string;
}

/**
 * The product itself. `offers` is only included when the page is showing those
 * prices, so the structured data cannot advertise a price a visitor cannot see.
 */
export function softwareApplicationJsonLd(options: {
  url: string;
  description: string;
  offers?: SoftwareOffer[];
}): JsonLd {
  const jsonLd: JsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: ORGANIZATION_NAME,
    url: options.url,
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Web',
    description: options.description,
    publisher: { '@id': ORGANIZATION_ID },
  };

  if (options.offers && options.offers.length > 0) {
    jsonLd.offers = options.offers.map((offer) => ({
      '@type': 'Offer',
      name: offer.name,
      price: String(offer.price),
      priceCurrency: offer.priceCurrency,
      url: options.url,
    }));
  }

  return jsonLd;
}

export function articleJsonLd(options: {
  url: string;
  headline: string;
  description: string;
  datePublished: string;
}): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: options.headline,
    description: options.description,
    datePublished: options.datePublished,
    url: options.url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': options.url },
    author: { '@id': ORGANIZATION_ID },
    publisher: { '@id': ORGANIZATION_ID },
  };
}

export interface BreadcrumbItem {
  name: string;
  /** Absolute URL, or omitted for the current page at the end of the trail. */
  url?: string;
}

/**
 * Only call this from a page that renders the same trail on screen: a
 * breadcrumb in search results that does not exist on the page is a mismatch
 * between structured data and visible navigation.
 */
export function breadcrumbJsonLd(items: BreadcrumbItem[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.url ? { item: item.url } : {}),
    })),
  };
}
